import * as React from "react";
import {useMemo, useState} from "react";
import {Box} from "@mui/material";
import {RequestFilter} from "@/api/data-contracts";
import {OrderFilter} from "@/model/order";
import {useTranslation} from "react-i18next";
import {withAuthWrapperComponent} from "@/app/auth/WithAuthWrapperComponent";
import {AppFrame} from "@/app/AppFrame";
import {AppPaths} from "@/app/AppPaths";
import {QuoteI18N} from "@/translations";
import {SectionedTable} from "@/pages/views/SectionedTable";
import {FullscreenTableDialog} from "@/pages/views/FullscreenTableDialog";
import {RequestOverviewTable} from "@/pages/request-overview-page/RequestOverviewTable";
import {OrderTrackingTable} from "@/pages/order-tracking-page/OrderTrackingTable";
import {useRequestOverviewState} from "@/common/overview-state/useRequestOverviewState";
import {useOrderOverviewState} from "@/common/overview-state/useOrderOverviewState";

const BidderOverviewPage = () => {
    const { t } = useTranslation();

    // Quote/request filter and rows — account-scoped view state (#1606); the request half is fully wired
    // to the shared filter bar, sortable column headers and pagination (#1607) via `requestState`.
    const requestState = useRequestOverviewState(RequestFilter.EXTERNAL);
    const [requestFullscreen, setRequestFullscreen] = useState(false);

    // Order filter and rows — account-scoped view state (#1606); the order half is fully wired to the
    // shared filter bar, sortable column headers and pagination (#1607) via `orderState`.
    const orderState = useOrderOverviewState(OrderFilter.EXTERNAL);
    const [orderFullscreen, setOrderFullscreen] = useState(false);

    // `requestState` carries a stable identity across renders that change none of its fields (memoized in
    // `useOverviewTableState`/`useRequestOverviewState`), so a single dependency is enough here — same
    // sharing rationale as `orderTableNode` below: one reference used by both `SectionedTable` and
    // `FullscreenTableDialog`.
    const requestTableNode = useMemo(
            () => (
                    <RequestOverviewTable
                            type={RequestFilter.EXTERNAL}
                            target={AppPaths.EXTERNAL_REQUEST_fn}
                            state={requestState}
                            fillHeight
                    />
            ),
            [requestState]
    );

    // `orderState` carries a stable identity across renders that change none of its fields (memoized in
    // `useOverviewTableState`/`useOrderOverviewState`), so a single dependency is enough here — same
    // sharing rationale as `requestTableNode` above: one reference used by both `SectionedTable` and
    // `FullscreenTableDialog`.
    const orderTableNode = useMemo(
            () => <OrderTrackingTable type={OrderFilter.EXTERNAL} state={orderState} fillHeight />,
            [orderState]
    );

    return (
        <AppFrame loading={false}>
            <Box display="flex" flexDirection="column" height="calc(100vh - 120px)" gap={2}>
                {/* Requests section — top 50% */}
                <Box flex={1} minHeight={240} overflow="hidden">
                    <SectionedTable
                            title={t(QuoteI18N.overview.page.requestsSectionTitle)}
                            fullscreenTooltip={t(QuoteI18N.overview.tooltip.fullscreen)}
                        tableNode={requestTableNode}
                        onFullscreen={() => setRequestFullscreen(true)}
                    />
                </Box>

                {/* Orders section — bottom 50% */}
                <Box flex={1} minHeight={240} overflow="hidden">
                    <SectionedTable
                            title={t(QuoteI18N.overview.page.ordersSectionTitle)}
                            fullscreenTooltip={t(QuoteI18N.overview.tooltip.fullscreen)}
                        tableNode={orderTableNode}
                        onFullscreen={() => setOrderFullscreen(true)}
                    />
                </Box>
            </Box>

            {/* Fullscreen dialog for requests */}
            <FullscreenTableDialog
                open={requestFullscreen}
                onClose={() => setRequestFullscreen(false)}
                title={t(QuoteI18N.overview.page.requestsSectionTitle)}
                closeLabel={t(QuoteI18N.overview.action.closeFullscreen)}
                tableNode={requestTableNode}
            />

            {/* Fullscreen dialog for orders */}
            <FullscreenTableDialog
                open={orderFullscreen}
                onClose={() => setOrderFullscreen(false)}
                title={t(QuoteI18N.overview.page.ordersSectionTitle)}
                closeLabel={t(QuoteI18N.overview.action.closeFullscreen)}
                tableNode={orderTableNode}
            />
        </AppFrame>
    );
};

export default withAuthWrapperComponent(BidderOverviewPage);
